import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { ref, uploadBytes } from 'firebase/storage'
import {
  COLLECTIONS,
  creditCardStatementSchema,
  statementImagePath,
  reconciliationPeriodSchema,
  type CreditCardStatement,
  type ReconciliationPeriod,
  type StatementCycle,
} from '@budgetmonkey/shared'
import { db, functions, storage } from '@/lib/firebase'
import { toIso } from '@/lib/firestore'

/**
 * Reconciliation periods — REQUIREMENTS.md §5.5, §7.2.
 *
 * One per statement cycle. The period owns `orderedTransactionIds`, which is
 * the packet's item order — §8.1 ties log sheet item numbers to the receipt
 * pages printed behind them, so that list is the single source of both.
 */

const TIMESTAMP_FIELDS = ['createdAt', 'updatedAt', 'submittedAt'] as const

export async function fetchPeriods(userId: string): Promise<ReconciliationPeriod[]> {
  const snapshot = await getDocs(
    query(
      collection(db, COLLECTIONS.reconciliationPeriods),
      where('userId', '==', userId),
      orderBy('cycleEnd', 'desc'),
    ),
  )

  // Parsed per document, for the same reason receipts are: one malformed
  // record must not blank the whole screen.
  const periods: ReconciliationPeriod[] = []
  for (const document of snapshot.docs) {
    const raw: Record<string, unknown> = { ...document.data(), id: document.id }
    for (const field of TIMESTAMP_FIELDS) {
      if (field in raw) raw[field] = toIso(raw[field])
    }
    const parsed = reconciliationPeriodSchema.safeParse(raw)
    if (parsed.success) periods.push(parsed.data)
    else console.warn('Skipping malformed reconciliation period', document.id, parsed.error)
  }
  return periods
}

export async function createPeriod(
  userId: string,
  cycle: StatementCycle,
  orderedTransactionIds: readonly string[],
): Promise<string> {
  const now = new Date()
  const created = await addDoc(collection(db, COLLECTIONS.reconciliationPeriods), {
    userId,
    cycleStart: cycle.start,
    cycleEnd: cycle.end,
    statementDate: null,
    statementId: null,
    orderedTransactionIds: [...orderedTransactionIds],
    unmatchedStatementLineIds: [],
    status: 'open',
    packetPdfUrl: null,
    packetXlsxUrl: null,
    submittedAt: null,
    createdAt: now,
    updatedAt: now,
  })
  return created.id
}

/** Reorder or add/remove items. This list *is* the packet order. */
export async function setPeriodItems(
  periodId: string,
  orderedTransactionIds: readonly string[],
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.reconciliationPeriods, periodId), {
    orderedTransactionIds: [...orderedTransactionIds],
    updatedAt: new Date(),
  })
}

/**
 * Discard a packet and the statement behind it.
 *
 * Needed because a failed-looking generation is easy to repeat — an "internal"
 * error that had in fact succeeded left three identical July packets — and
 * there was no way to clear the duplicates.
 *
 * The transactions are untouched. They are the ledger; the packet is a view of
 * them, and deleting a view must never delete the record. What does go is the
 * statement, which exists only to drive this packet, and the `statementVendorName`
 * it stamped — leaving that behind would print a spelling from a statement no
 * longer in the system.
 */
export async function deletePeriod(periodId: string): Promise<void> {
  const period = await getDoc(doc(db, COLLECTIONS.reconciliationPeriods, periodId))
  if (!period.exists()) return

  const statementId = period.get('statementId') as string | null
  const transactionIds = (period.get('orderedTransactionIds') as string[] | null) ?? []

  const batch = writeBatch(db)
  batch.delete(doc(db, COLLECTIONS.reconciliationPeriods, periodId))
  if (statementId !== null) {
    batch.delete(doc(db, COLLECTIONS.creditCardStatements, statementId))
  }
  // A charge confirmed *by this packet* loses that confirmation with it —
  // otherwise the ledger claims the business office accepted something on a
  // packet no longer in the system. A charge confirmed by a CSIU import is
  // untouched: that is the other path, and it did not depend on this.
  const charges = await Promise.all(
    transactionIds.map((id) => getDoc(doc(db, COLLECTIONS.transactions, id))),
  )
  for (const charge of charges) {
    if (!charge.exists()) continue
    const confirmedHere = charge.get('reconciledVia') === 'packet'
    batch.update(charge.ref, {
      statementVendorName: null,
      ...(confirmedHere
        ? {
            reconciledVia: null,
            reconciledAt: null,
            ...(charge.get('status') === 'reconciled' ? { status: 'pending' } : {}),
          }
        : {}),
      updatedAt: new Date(),
    })
  }
  await batch.commit()
}

/**
 * Hand the packet in — and confirm the charges it carried.
 *
 * §5.3: a P-Card charge has exactly one confirmation path, and this is it.
 * Submitting only marked the period and left every charge on it unconfirmed,
 * so the badges read "Unconfirmed" forever and the dashboard's *Confirmed*
 * total stayed at zero for P-Card spending no matter how many packets went
 * out. The whole point of the card is to answer "what has the business office
 * actually accepted", and it was answering nothing.
 *
 * Written in one batch with the period, because a packet recorded as submitted
 * whose charges disagree is precisely the drift that made this invisible.
 *
 * Deliberately conservative about `status`. `reconciledVia` is a fact — this
 * charge went out on a submitted packet — and is set for everything except a
 * void. But `status` is only promoted from `pending`: a charge marked disputed
 * or awaiting refund documentation is saying something the packet does not
 * override, and flattening it to `reconciled` would erase it.
 *
 * Idempotent, so it also repairs a packet submitted before this existed.
 */
export async function markSubmitted(periodId: string): Promise<void> {
  const periodRef = doc(db, COLLECTIONS.reconciliationPeriods, periodId)
  const period = await getDoc(periodRef)
  if (!period.exists()) return

  const transactionIds =
    (period.get('orderedTransactionIds') as string[] | null) ?? []
  const charges = await Promise.all(
    transactionIds.map((id) => getDoc(doc(db, COLLECTIONS.transactions, id))),
  )

  const now = new Date()
  const batch = writeBatch(db)
  batch.update(periodRef, { status: 'submitted', submittedAt: now, updatedAt: now })

  for (const charge of charges) {
    if (!charge.exists()) continue
    const status = charge.get('status') as string
    if (status === 'void') continue
    batch.update(charge.ref, {
      reconciledVia: 'packet',
      reconciledAt: now,
      ...(status === 'pending' ? { status: 'reconciled' } : {}),
      updatedAt: now,
    })
  }

  await batch.commit()
}

/**
 * The statement behind a period, with its lines.
 *
 * Parsed rather than trusted: a statement that fails its schema must show as
 * absent, not blank the packet screen — the same rule receipts follow.
 */
export async function fetchStatement(
  statementId: string,
): Promise<CreditCardStatement | null> {
  const snapshot = await getDoc(doc(db, COLLECTIONS.creditCardStatements, statementId))
  if (!snapshot.exists()) return null

  const raw: Record<string, unknown> = { ...snapshot.data(), id: snapshot.id }
  for (const field of ['photographedAt', 'processedAt'] as const) {
    if (field in raw) raw[field] = toIso(raw[field])
  }

  const parsed = creditCardStatementSchema.safeParse(raw)
  if (!parsed.success) {
    console.warn('Skipping malformed statement', statementId, parsed.error)
    return null
  }
  return parsed.data
}

/**
 * Attach a transaction to a statement line, and re-derive the packet order.
 *
 * Order is recomputed from the statement rather than appended to, because the
 * statement *is* the order (§5.5): a charge captured last still belongs at the
 * line number the statement printed it on, and the business office reconciles
 * against that. Both documents move together so the two can never disagree.
 */
export async function linkStatementLine(
  statementId: string,
  periodId: string,
  lineIndex: number,
  transactionId: string | null,
): Promise<void> {
  const statement = await fetchStatement(statementId)
  if (statement === null) throw new Error('That statement could not be read.')

  const lines = statement.lines.map((line) =>
    line.lineIndex === lineIndex
      ? { ...line, matchedTransactionId: transactionId, userConfirmed: true }
      : line,
  )

  const batch = writeBatch(db)
  batch.update(doc(db, COLLECTIONS.creditCardStatements, statementId), { lines })

  // The log sheet prints the statement's spelling, not the captured one
  // (§8.1), so the charge carries it from the moment the line claims it.
  // Without this the packet falls back to whatever was typed.
  if (transactionId !== null) {
    const line = lines.find((l) => l.lineIndex === lineIndex)
    if (line !== undefined) {
      batch.update(doc(db, COLLECTIONS.transactions, transactionId), {
        statementVendorName: line.merchantAsPrinted,
        updatedAt: new Date(),
      })
    }
  }
  batch.update(doc(db, COLLECTIONS.reconciliationPeriods, periodId), {
    orderedTransactionIds: lines
      .map((line) => line.matchedTransactionId)
      .filter((id): id is string => id !== null),
    unmatchedStatementLineIds: lines
      .filter((line) => line.matchedTransactionId === null)
      .map((line) => String(line.lineIndex)),
    updatedAt: new Date(),
  })
  await batch.commit()
}

export interface IngestResult {
  periodId: string
  statementId: string
  cycleLabel: string
  statementTotal: number | null
  total: number
  matched: number
  amountOnly: number
  unmatched: number
}

/**
 * Upload the photographed statement pages, then read them.
 *
 * Pages go to Storage under a batch id generated here, before any function
 * runs — the same ordering §9 requires of receipts. If extraction fails, the
 * photographs are already durable and the attempt can be repeated without
 * asking Matt to re-photograph a paper statement he may no longer have to
 * hand.
 */
export async function uploadStatementPages(
  userId: string,
  files: readonly File[],
  onProgress?: (done: number, total: number) => void,
): Promise<string[]> {
  const batchId = crypto.randomUUID()
  const paths: string[] = []

  // Sequential, so page order is the order they were chosen. Uploading in
  // parallel would finish in size order and scramble a multi-page statement.
  for (const [index, file] of files.entries()) {
    const path = statementImagePath(userId, batchId, index + 1)
    await uploadBytes(ref(storage, path), file, { contentType: file.type })
    paths.push(path)
    onProgress?.(index + 1, files.length)
  }

  return paths
}

export async function ingestStatement(pagePaths: readonly string[]): Promise<IngestResult> {
  const call = httpsCallable<{ pagePaths: string[] }, IngestResult>(
    functions,
    'ingestStatement',
  )
  const result = await call({ pagePaths: [...pagePaths] })
  return result.data
}

export interface PacketResult {
  /** The PDF. */
  path: string
  xlsxPath: string
  pages: number
  items: number
  grandTotal: number
}

/**
 * Build the packet PDF. Server-side: it needs headless Chrome, and the packet
 * is the authoritative submission, so it is assembled from stored data rather
 * than from what a browser tab happens to be showing.
 */
export async function generatePacket(periodId: string): Promise<PacketResult> {
  const call = httpsCallable<{ periodId: string }, PacketResult>(
    functions,
    'generatePacket',
  )
  const result = await call({ periodId })
  return result.data
}
